import { normalizeBusinessType } from './businessType';

export const UNIVERSAL_ROLES = ['Manager', 'Cashier', 'None'] as const;

export const BUSINESS_TYPE_ROLES: Record<string, readonly string[]> = {
  'Hotel & Restaurant': ['Chef', 'Waiter'],
  Bar: ['Bartender', 'Mixologist', 'Waiter', 'Bouncer/Door Security'],
  'Wines & Spirits': [],
  Chemist: ['Pharmacist', 'Pharmacy Technician'],
  Boutique: [],
  Cafe: ['Barista', 'Waiter', 'Baker', 'Runner', 'Dishwasher'],
  Bakery: ['Baker', 'Chef'],
  Butchery: ['Butcher', 'Meat Packer', 'Cleaner'],
  'Grocery / Mini Mart': [],
  Supermarket: [],
  'Electronics & Mobile': ['Technician'],
  Hardware: ['Loader', 'Paint Mixer'],
  'Auto Parts & Garage': ['Mechanic'],
  'Salon & Spa': ['Hair Stylist', 'Beautician', 'Nail Technician'],
  Barbershop: ['Barber'],
  'Car Wash': ['Car Washer', 'Detailer'],
  'Stationery & Printing': ['Sales Person', 'Storekeeper', 'Stock Controller'],
  Bookshop: ['Sales Person', 'Storekeeper', 'Stock Controller'],
  'Furniture & Upholstery': ['Carpenter'],
  'Laundry & Dry Cleaner': ['Washer'],
  Agrovet: ['Veterinary'],

  // Keys aligned to BUSINESS_TYPES in CONSTANTS.ts (canonical)
  'Bar & Restaurant': [
    'Chef',
    'Waiter',
    'Bartender',
    'Mixologist',
    'Bouncer/Door Security',
  ],
  Pharmacy: ['Pharmacist', 'Pharmacy Technician'],
  BookShop: ['Sales Person', 'Storekeeper', 'Stock Controller'],
  'General Electronics': ['Technician'],
  'Hardware Store': ['Loader', 'Paint Mixer'],
  'Wines and Spirits': [],
  AutoSpares: ['Mechanic'],
  'Coffee Shop': ['Barista', 'Waiter', 'Baker', 'Runner', 'Dishwasher'],
  Hotel: ['Chef', 'Waiter'],
  'Beauty & Cosmetics': ['Hair Stylist', 'Beautician', 'Nail Technician'],
  'Bakery & Pastry': ['Baker', 'Chef'],
  'Boutique & Apparel': [],
  'General Shop': ['Sales Person', 'Storekeeper', 'Stock Controller'],
  'Gift Shop': ['Sales Person', 'Storekeeper', 'Stock Controller'],
  'Jewelry Store': ['Sales Person', 'Storekeeper', 'Stock Controller'],
  Kiosk: ['Sales Person'],
  Grocery: [],
  Store: ['Sales Person', 'Storekeeper', 'Stock Controller'],
  'Cereals & Grains': [
    'Sales Person',
    'Storekeeper',
    'Loader',
    'Stock Controller',
  ],
  'Phone Accessories & Repairs': ['Technician'],
  'Fruits & Vegetables Vendor': ['Vendor', 'Porter/Loader'],
  'Milk ATM': ['Milk Attendant', 'Cleaner'],
  'Furniture and Fittings': ['Carpenter', 'Installer', 'Loader'],
  'Home & Kitchen': ['Sales Person', 'Storekeeper'],
  'Mini Mart': ['Sales Person', 'Storekeeper', 'Stock Controller'],
};

export const getRolesForType = (businessType?: string | null): string[] => {
  const base = [...UNIVERSAL_ROLES];
  const canonical = normalizeBusinessType(businessType);
  const extras = canonical ? (BUSINESS_TYPE_ROLES[canonical] ?? []) : [];
  const merged = [...base, ...extras];
  // Ensure uniqueness and keep order
  const seen = new Set<string>();
  for (const r of merged) {
    if (!seen.has(r)) seen.add(r);
  }
  return Array.from(seen);
};

export type RoleInfo = {
  title: string;
  summary?: string;
  highlights?: string[];
};

// Core role information shown in role info modal (web + mobile parity)
export const ROLE_INFO: Record<string, RoleInfo> = {
  Owner: {
    title: 'Owner',
    summary:
      'Full admin operational control with admin access and functionality for all business locations.',
    highlights: [
      'Add/update employees, locations and products',
      'View sales and expense reports',
      'Override prices, discounts and orders',
    ],
  },
  Manager: {
    title: 'Manager',
    summary:
      'Full operational control with access to reports, staff and sensitive actions for a specific business location.',
    highlights: [
      'Add/update employees',
      'View sales and expense reports',
      'Override prices, discounts and orders',
    ],
  },
  Cashier: {
    title: 'Cashier',
    summary:
      'Handles POS sales and payments with limited permissions for day-to-day transactions for a specific business location.',
    highlights: [
      'Create sales & accept payments',
      'Issue receipts',
      'No access to settings or staff management',
    ],
  },

  Chef: {
    title: 'Chef',
    summary:
      'Works in the kitchen to prepare orders and update order ticket statuses for a specific business location.',
    highlights: [
      'View incoming kitchen order tickets',
      'Mark items as preparing / ready',
      'Access to Kitchen module and No access to POS',
    ],
  },
  Waiter: {
    title: 'Waiter',
    summary:
      'Takes customer orders on POS for a specific business location, but cannot complete payments or checkout.',
    highlights: [
      'Create and edit orders on POS',
      'Save & send orders to cashier',
      'No access to checkout or payments',
    ],
  },
};

/** Lowercase trimmed employee role for comparisons (API may send any casing). */
export const normalizeEmployeeRole = (role?: string | null): string =>
  String(role ?? '')
    .trim()
    .toLowerCase();

/** True owner account only (not manager/supervisor). Prefer isEmployeeAdminRole for UI gates. */
export const isEmployeeOwnerRole = (role?: string | null): boolean =>
  normalizeEmployeeRole(role) === 'owner';

/** Owner, Manager, or Supervisor admin-style UI access. */
export const isEmployeeAdminRole = (role?: string | null): boolean => {
  const n = normalizeEmployeeRole(role);
  return n === 'owner' || n === 'manager' || n === 'supervisor';
};

/** Waiter role. Sees POS but cannot complete a sale. */
export const isEmployeeWaiterRole = (role?: string | null): boolean =>
  normalizeEmployeeRole(role) === 'waiter';

/** Can this employee complete a sale (take payment / checkout)? */
export const canEmployeeCheckout = (role?: string | null): boolean =>
  !isEmployeeWaiterRole(role);

/**
 * Can this employee print or share a receipt? The staff who take payment, as
 * the server allows: it records who issued the receipt, so a payment that
 * lands later with no one at the till reaches them. Waiters cannot.
 */
export const canEmployeeIssueReceipt = (role?: string | null): boolean =>
  canEmployeeCheckout(role);

/** Can this role create customers? All roles can. */
export const canCreateCustomer = (_role?: string | null): boolean => true;

/** Can this role update (edit) customers? All except Waiter. */
export const canUpdateCustomer = (role?: string | null): boolean =>
  !isEmployeeWaiterRole(role);

/** Can this role delete customers? Only admin roles (Owner/Manager/Supervisor). */
export const canDeleteCustomer = (role?: string | null): boolean =>
  isEmployeeAdminRole(role);
